package com.veldekar.components.layouts

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.ColumnScope
import com.varabyte.kobweb.compose.ui.Alignment.CenterHorizontally
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxSize
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gridRow
import com.varabyte.kobweb.compose.ui.modifiers.gridTemplateRows
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.core.data.getValue
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.veldekar.components.metadata.updateOpenGraphMetadata
import com.veldekar.components.sections.Footer
import com.veldekar.components.sections.NavHeader
import com.veldekar.toSitePalette
import kotlinx.browser.document
import kotlinx.datetime.LocalDate
import kotlinx.datetime.number
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.fr
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vh
import org.jetbrains.compose.web.dom.Main

// Standard "sr-only" / visually-hidden pattern: keeps content in the accessibility tree
// while making it invisible to sighted users.
val VisuallyHiddenStyle = CssStyle.base {
    Modifier.styleModifier {
        property("position", "absolute")
        property("width", "1px")
        property("height", "1px")
        property("padding", "0")
        property("margin", "-1px")
        property("overflow", "hidden")
        property("clip", "rect(0, 0, 0, 0)")
        property("white-space", "nowrap")
        property("border", "0")
    }
}

val PageContentStyle = CssStyle {
    base { Modifier.fillMaxSize().padding(leftRight = 2.cssRem, top = 0.5.cssRem) }
    Breakpoint.MD { Modifier.maxWidth(60.cssRem) }
}

// SunderedShield SVG graphic with radial gradient fade-out effect
@Composable
private fun SvgSunderedShield(modifier: Modifier) {
    @Suppress("MaxLineLength")
    val svgDataUri =
        "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='740.906' height='746.146' viewBox='0 0 740.906 746.146'%3E%3Cg style='display:inline;opacity:1'%3E%3Cpath d='M912.068 150.838v.035c-19.666.392-181.337 4.814-349.957 46.223-42.439 349.09 81.742 528.527 193.928 617.761h.002l81.574-21.15 5.067-81.059 60.795-35.462-20.266-77.684 37.152-97.945-32.086-70.928 42.22-97.947-43.91-67.55 40.552-113.884c-6.263-.222-10.7-.328-13.07-.375v-.035s-.339.004-1 .015c-.662-.011-1-.015-1-.015' style='fill:%23000;fill-opacity:1;stroke:none;stroke-width:3;stroke-linecap:round;stroke-opacity:1' transform='translate(-553.591 -150.838)'/%3E%3Cpath d='M912.05 171.104v.033c-18.553.37-171.076 4.542-330.155 43.607-41.903 344.673 88.368 514.038 197.648 594.02h.002l58.07-15.057 5.067-81.059 60.795-35.462-20.266-77.684 37.152-97.945-32.086-70.928 42.22-97.947-43.91-67.55 33.415-93.845a669 669 0 0 0-6.066-.15v-.033s-.32.002-.944.013c-.606-.01-.923-.013-.941-.013' style='fill:%23303030;fill-opacity:1;stroke:none;stroke-width:2.83026;stroke-linecap:round;stroke-opacity:1' transform='translate(-553.591 -150.838)'/%3E%3Cpath d='M903.73 216.986c-35.79 1.215-154.287 7.385-277.3 37.594-41.302 339.734 110.098 483.256 208.709 539.77l2.476-.643 5.067-81.059 60.795-35.462-20.266-77.684 37.152-97.945-32.086-70.928 42.22-97.947-43.91-67.55z' style='fill:%23000;fill-opacity:1;stroke:none;stroke-width:2.45678;stroke-linecap:round;stroke-opacity:1' transform='translate(-553.591 -150.838)'/%3E%3Cpath d='M897.012 235.86c-40.883 1.745-144.01 8.579-250.778 34.798C607.977 585.354 746.742 719.801 838.88 773.49l3.803-60.842 60.795-35.462-20.266-77.684 37.152-97.945-32.086-70.928 42.22-97.947-43.91-67.55z' style='fill:%23818181;fill-opacity:1;stroke:none;stroke-width:2.29471;stroke-linecap:round;stroke-opacity:1' transform='translate(-553.591 -150.838)'/%3E%3C/g%3E%3Cg style='display:inline;opacity:1'%3E%3Cpath d='m927.139 151.248-40.551 113.885 43.908 67.549-42.219 97.947 32.086 70.928-37.152 97.945 20.266 77.684-60.795 35.462-5.067 81.059-81.574 21.15c81.691 64.979 157.021 82.127 157.021 82.127l.006-4.345.006 4.345s424.293-96.594 350.95-699.888c-148.29-36.416-291.203-44.227-336.885-45.848Z' style='fill:%23000;fill-opacity:1;stroke:none;stroke-width:3;stroke-linecap:round;stroke-opacity:1' transform='translate(-531.638 -150.838)'/%3E%3Cpath d='m920.002 171.287-33.414 93.846 43.908 67.549-42.219 97.947 32.086 70.928-37.152 97.945 20.266 77.684-60.795 35.462-5.067 81.059-58.07 15.057c71.174 52.091 133.443 66.267 133.443 66.267l.004-4.1.006 4.1s400.285-91.128 331.092-660.287c-147.591-36.244-289.538-42.454-324.086-43.457z' style='fill:%23303030;fill-opacity:1;stroke:none;stroke-width:2.83026;stroke-linecap:round;stroke-opacity:1' transform='translate(-531.638 -150.838)'/%3E%3Cpath d='M913.018 216.7v.029c-1.759.035-4.902.108-9.288.257l-17.142 48.147 43.908 67.549-42.219 97.947 32.086 70.928-37.152 97.945 20.266 77.684-60.795 35.462-5.067 81.059-2.476.643c44.792 25.67 78.693 33.388 78.693 33.388l.004-3.558.004 3.558s347.465-79.104 287.402-573.158c-138.087-33.91-270.482-37.53-286.588-37.851v-.03s-.276.002-.818.012c-.52-.01-.797-.012-.818-.012z' style='fill:%23000;fill-opacity:1;stroke:none;stroke-width:2.45678;stroke-linecap:round;stroke-opacity:1' transform='translate(-531.638 -150.838)'/%3E%3Cpath d='M913.916 235.275v.028c-2.59.051-8.4.193-16.904.556l-10.424 29.274 43.908 67.549-42.219 97.947 32.086 70.928-37.152 97.945 20.266 77.684-60.795 35.462-3.803 60.842c42.97 25.04 75.799 32.514 75.799 32.514l.004-3.324.004 3.324s324.541-73.885 268.441-535.346c-128.977-31.673-252.639-35.055-267.682-35.355v-.028q-.002 0-.763.012c-.49-.009-.75-.011-.766-.012z' style='fill:%23818181;fill-opacity:1;stroke:none;stroke-width:2.29471;stroke-linecap:round;stroke-opacity:1' transform='translate(-531.638 -150.838)'/%3E%3C/g%3E%3C/svg%3E"

    // For dark mode, use a subtle combination of inversion and reduced opacity
    val filterValue = if (ColorMode.current == DARK) {
        "invert(1) opacity(0.12)"
    } else {
        "opacity(0.08)"
    }

    Image(
        src = svgDataUri,
        alt = "",
        modifier = modifier
            .width(12.cssRem)
            .height(12.084.cssRem)
            .maxWidth(100.percent)
            .styleModifier {
                property("filter", filterValue)
                property("mask-image", "radial-gradient(circle at 0% 0%, rgba(0,0,0,1) 50%, rgba(0,0,0,0) 120%)")
                property(
                    "-webkit-mask-image",
                    "radial-gradient(circle at 0% 0%, rgba(0,0,0,1) 50%, rgba(0,0,0,0) 120%)"
                )
                property("pointer-events", "none")
                property("-webkit-user-drag", "none")
                property("user-select", "none")
            }
    )
}

@Suppress("LongParameterList")
class PageLayoutData(
    val title: String,
    val date: LocalDate? = null,
    val ogTitle: String? = null,
    val ogDescription: String? = null,
    val ogImage: String? = null,
    val ogImageWidth: Int? = null,
    val ogImageHeight: Int? = null,
    val ogImageAlt: String? = null,
    val aiHint: String? = null,
    val includeSrdAttribution: Boolean = false,
    val isModal: Boolean = false,
    val showModalCard: Boolean = true,
)

@Composable
@Layout
fun PageLayout(ctx: PageContext, content: @Composable ColumnScope.() -> Unit) {
    val data = ctx.data.getValue<PageLayoutData>()
    PageLayout(ctx, data, content)
}

@Composable
fun PageLayout(ctx: PageContext, data: PageLayoutData, content: @Composable ColumnScope.() -> Unit) {
    // Set the page title, preferring localized date if available
    LaunchedEffect(data.title, data.date) {
        if (data.date != null) {
            val date = data.date
            // Month is 0-indexed in JS Date
            val jsDate = kotlin.js.Date(date.year, date.month.number - 1, date.day)
            val options = js("({ year: 'numeric', month: 'long', day: 'numeric' })")
            val localizedDate = jsDate.asDynamic().toLocaleDateString(undefined, options) as String
            document.title = "Veldekar - $localizedDate"
        } else {
            document.title = "Veldekar - ${data.title}"
        }
    }

    // OpenGraph metadata management
    LaunchedEffect(data.title, data.ogTitle, data.ogDescription, data.ogImage, data.ogImageWidth, data.ogImageHeight, data.ogImageAlt, data.aiHint) {
        updateOpenGraphMetadata(
            title = data.ogTitle ?: "Veldekar - ${data.title}",
            description = data.ogDescription,
            image = data.ogImage,
            imageWidth = data.ogImageWidth,
            imageHeight = data.ogImageHeight,
            imageAlt = data.ogImageAlt,
            aiHint = data.aiHint,
        )
    }

    Box(
        Modifier
            .fillMaxWidth()
            .minHeight(100.vh)
            .gridTemplateRows {
                size(1.fr)
                size(minContent)
            },
        contentAlignment = Center
    ) {
        SvgSunderedShield(Modifier.gridRow(1).align(TopStart))

        if (data.isModal) {
            Box(
                Modifier
                    .fillMaxSize()
                    .backgroundColor(ColorMode.current.toSitePalette().nearBackground.toRgb().let {
                        com.varabyte.kobweb.compose.ui.graphics.Color.rgba(it.red, it.green, it.blue, 0.8f)
                    })
                    .gridRow(1)
            )
        }

        Column(
            Modifier.fillMaxSize().gridRow(1),
            horizontalAlignment = CenterHorizontally,
        ) {
            NavHeader(ctx.route.path)
            Main(PageContentStyle.toAttrs()) {
                if (data.isModal && data.showModalCard) {
                    val palette = ColorMode.current.toSitePalette()
                    Column(
                        Modifier
                            .fillMaxWidth()
                            .padding(2.cssRem)
                            .backgroundColor(palette.nearBackground)
                            .border(1.px, Solid, palette.brand.primary)
                            .borderRadius(8.px)
                    ) {
                        content()
                    }
                } else {
                    content()
                }
            }
        }
        // Associate the footer with the row that will get pushed off the bottom of the page if it can't fit.
        Footer(Modifier.fillMaxWidth().gridRow(2), data.includeSrdAttribution)
    }
}
